// src/context/AppContext.js
//
// AuthProvider real para ET EducaT — reemplaza el usersDB en memoria
// de authStore.js. Sigue el mismo patrón que news_frontend/AppContext.js:
//   1. login(email, password) → POST Ecolab /api/ecolab/auth/login
//   2. Ecolab responde { token, refreshToken, usuario }
//   3. Se guarda el token en localStorage → api.js lo adjunta como
//      Authorization: Bearer a cada llamada al backend .NET.
//   4. El backend .NET valida la firma con Token:Key compartida y
//      aprovisiona la fila en educat_main.usuarios_cache automáticamente
//      (EnsureEducaTUserFilter) — no hay que crear el usuario a mano.
//   5. refreshSession(): cuando el token expira, canjea el refreshToken.
//   6. Tras el login, se llama a GET /me para traer el ROL real asignado
//      en EducaT (Administrador / Instructor / Estudiante) — el JWT de
//      Ecolab NO trae ese rol, es local de esta plataforma.
//
// MIGRACIÓN DESDE authStore.js (Zustand):
//   - Los componentes que hacían `useAuthStore()` deben cambiar a
//     `useAuth()` de este archivo.
//   - `user.role` sigue siendo 'admin' | 'instructor' | 'student' (se
//     traduce automáticamente desde Administrador/Instructor/Estudiante).
//   - `isAuthenticated`, `login`, `logout` tienen la misma forma.
//   - Los helpers `isAdmin()`, `isInstructor()`, `isStudent()` del store
//     viejo se reemplazan por `hasRole('admin')`, etc.
//   - El CRUD de usuarios que vivía en authStore (createStudent,
//     addCourseToInstructor, getStats...) ahora vive en adminService
//     (api.js) — son operaciones de backend real, no de estado local.

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, meService, IS_REDEPROG } from '../services/api';

const ECOLAB_API_URL =
  process.env.REACT_APP_ECOLAB_API_URL ||
  'https://ecolab-backend-723916953839.us-central1.run.app';

// Fase 5 Redeprog SaaS: en modo Redeprog el login/refresh va contra EducaT
// local, no contra Ecolab. Esta constante se usa dentro de login() y
// refreshSession() para elegir el endpoint correcto sin bifurcar toda la
// función.
const AUTH_BASE_URL = IS_REDEPROG
  ? (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/v1/educat'
  : ECOLAB_API_URL + '/api/ecolab';

const TOKEN_KEY   = 'educatToken';
const REFRESH_KEY = 'educatRefreshToken';
const USER_KEY    = 'educatUser';

// Traduce el rol de EducaT (backend) al valor corto que ya usan las
// rutas y componentes del frontend actual (AdminRoute, InstructorRoute...).
// Acepta el rol del backend (Administrador/Instructor/Estudiante) o el que
// ya viene traducido por meService.getPerfil (admin/instructor/student).
// Antes solo aceptaba el del backend y, como getPerfil ya lo traduce, todo
// usuario quedaba como 'student' después de refrescar el perfil.
const mapRol = (rol) => {
  const map = {
    Administrador: 'admin', Instructor: 'instructor', Estudiante: 'student',
    admin: 'admin', instructor: 'instructor', student: 'student',
  };
  return map[rol] || 'student';
};

// Decodifica el payload de un JWT sin verificar firma (solo lectura).
// Mismo fix de UTF-8 que news_frontend: atob() devuelve binary string,
// hay que reconstruir bytes reales antes de decodificar como UTF-8 o
// los acentos (López, Muñoz) salen mal.
const decodeJwt = (jwt) => {
  try {
    const [, payload] = jwt.split('.');
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const binary = atob(b64 + pad);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const jsonUtf8 = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(jsonUtf8);
  } catch {
    return null;
  }
};

// Arma el objeto user base a partir del JWT de Ecolab. El rol de
// EducaT se completa después con GET /me (Ecolab no lo conoce).
//
// ecolabRoles: roles CROSS-ECOSISTEMA (SuperAdmin, Aliado, Afiliado, etc.)
// -- vienen del claim "role" del JWT de Ecolab (ClaimTypes.Role se
// serializa como "role" en el JWT). Distinto del "role" de EducaT
// (admin/instructor/student) que se completa después vía GET /me --
// son dos vocabularios independientes, ver ECOSISTEMA_IDENTIDAD_Y_ROLES.md.
const buildUserFromJwt = (jwt, extra = {}) => {
  const p = decodeJwt(jwt) || {};
  const rawRole = p.role;
  const ecolabRoles = Array.isArray(rawRole) ? rawRole : (rawRole ? [rawRole] : []);
  return {
    id:     p.nameid || p.sub || extra.id || null,
    email:  p.email || extra.email || null,
    name:   [p.name, p.family_name].filter(Boolean).join(' ') || extra.name || p.email || null,
    role:   extra.role || 'student', // valor provisional hasta que GET /me responda
    avatar: extra.avatar || null,
    exp:    p.exp,
    ecolabRoles,
    // Fase 3+6 Redeprog SaaS: claim organizacion_id emitido por
    // TokenServiceEducaT. Ausente en tokens de Ecolab (modo Fenagro) y
    // en tokens del SuperAdmin de Redeprog (que vive por encima).
    organizacionId: p.organizacion_id || null,
  };
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Hidratar desde localStorage al montar ──────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const savedToken = localStorage.getItem(TOKEN_KEY);
        const savedUser  = localStorage.getItem(USER_KEY);
        if (savedToken) {
          setToken(savedToken);
          if (savedUser) {
            try { setUser(JSON.parse(savedUser)); }
            catch { setUser(buildUserFromJwt(savedToken)); }
          } else {
            setUser(buildUserFromJwt(savedToken));
          }
          // Refrescar el perfil real (incluye el rol de EducaT) en
          // segundo plano, sin bloquear el primer render.
          refreshProfileInternal(savedToken);
        }
      } catch (e) {
        console.warn('[AppContext] localStorage no disponible:', e.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync cross-tab: logout global vía evento 'storage' ──────────────
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === TOKEN_KEY && e.newValue === null) {
        setUser(null);
        setToken(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ── login(email, password, turnstileToken) → auth backend según modo ─
  const login = useCallback(async (email, password, turnstileToken) => {
    try {
      const body = IS_REDEPROG
        ? { email, password }
        : { email, password, turnstileToken };
      const resp = await fetch(`${AUTH_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return { success: false, error: err.mensaje || err.message || `Error ${resp.status}` };
      }

      const data = await resp.json();
      const accessToken  = data.token || data.accessToken || data.access_token;
      const refreshToken = data.refreshToken || data.refresh_token;

      if (!accessToken) {
        console.error('[AppContext] Backend respondió 200 pero sin JWT. Keys:', Object.keys(data));
        return { success: false, error: 'El servidor no devolvió un token válido.' };
      }

      const userData = data.usuario || data.user || {};
      let sessionUser = buildUserFromJwt(accessToken, {
        id: userData.id, email: userData.email,
        name: userData.nombre ? `${userData.nombre} ${userData.apellido || ''}`.trim() : undefined,
        avatar: userData.fotoUrl,
      });

      setToken(accessToken);
      setUser(sessionUser);
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(USER_KEY, JSON.stringify(sessionUser));
      if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);

      // Traer el perfil real de EducaT (rol, foto, municipio...) —
      // esto también dispara EnsureEducaTUserFilter en el backend,
      // que crea la fila en usuarios_cache si es la primera vez.
      const perfilCompleto = await refreshProfileInternal(accessToken);
      if (perfilCompleto) sessionUser = perfilCompleto;

      return { success: true, user: sessionUser };
    } catch (err) {
      console.error('[AppContext] login error:', err);
      return { success: false, error: 'No se pudo contactar al servidor.' };
    }
  }, []);

  // ── logout ────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    authService.logout();
  }, []);

  // ── refresh session con refreshToken ────────────────────────────────
  const refreshSession = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) { logout(); return false; }
    try {
      const resp = await fetch(`${AUTH_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!resp.ok) { logout(); return false; }
      const data = await resp.json();
      const newToken   = data.token || data.accessToken || data.access_token;
      const newRefresh = data.refreshToken || data.refresh_token;
      if (!newToken) { logout(); return false; }
      setToken(newToken);
      setUser((prev) => buildUserFromJwt(newToken, prev || {}));
      localStorage.setItem(TOKEN_KEY, newToken);
      if (newRefresh) localStorage.setItem(REFRESH_KEY, newRefresh);
      return true;
    } catch {
      logout();
      return false;
    }
  }, [logout]);

  // ── refrescar perfil desde el backend .NET (incluye rol de EducaT) ──
  const refreshProfileInternal = async (jwt) => {
    try {
      const savedToken = jwt || localStorage.getItem(TOKEN_KEY);
      if (!savedToken) return null;
      // meService.getPerfil() usa fetchApi() internamente, que ya lee
      // el token de localStorage — por eso no hace falta pasarlo.
      const perfil = await meService.getPerfil();
      if (!perfil) return null;

      const enriched = {
        id: perfil.id, email: perfil.email, name: perfil.name,
        avatar: perfil.avatar, role: mapRol(perfil.role) === 'student' && perfil.role
          ? mapRol(perfil.role) : mapRol(perfil.role),
        phone: perfil.phone, municipality: perfil.municipio, department: perfil.departamento,
        // ecolabRoles no viene de GET /me (Ecolab no lo expone ahí) --
        // se preserva del JWT ya decodificado, para no perderlo en cada
        // refresh de perfil.
        ecolabRoles: (() => {
          const p = decodeJwt(savedToken) || {};
          return Array.isArray(p.role) ? p.role : (p.role ? [p.role] : []);
        })(),
      };
      // El backend ya devuelve el rol mapeable directo (Administrador/Instructor/Estudiante)
      enriched.role = mapRol(perfil.role);

      setUser(enriched);
      try { localStorage.setItem(USER_KEY, JSON.stringify(enriched)); } catch { /* noop */ }
      return enriched;
    } catch (e) {
      console.warn('[AppContext] refreshProfile falló:', e.message);
      return null;
    }
  };

  const refreshProfile = useCallback(() => refreshProfileInternal(token), [token]);

  const isAuthenticated = !!user && !!token;

  const hasRole = useCallback((roleName) => !!user && user.role === roleName, [user]);

  // Roles CROSS-ECOSISTEMA de Ecolab (SuperAdmin, Aliado, Afiliado...) --
  // distintos del hasRole de arriba, que es el rol de EducaT.
  const hasEcolabRole = useCallback(
    (roleName) => !!user && Array.isArray(user.ecolabRoles) && user.ecolabRoles.includes(roleName),
    [user]
  );
  const isFenagroSuperAdmin = useCallback(() => hasEcolabRole('SuperAdmin'), [hasEcolabRole]);

  // Helpers equivalentes a los del authStore.js viejo, para minimizar
  // cambios en componentes que ya usaban isAdmin()/isInstructor()/isStudent().
  const isAdmin      = useCallback(() => hasRole('admin'), [hasRole]);
  const isInstructor = useCallback(() => hasRole('instructor'), [hasRole]);
  const isStudent    = useCallback(() => hasRole('student'), [hasRole]);

  // Fase 6 Redeprog SaaS: SuperAdmin de Redeprog. Es admin global de la
  // plataforma — vive por encima de la tenancy. Se identifica por:
  //   * IS_REDEPROG === true  (solo aplica en modo Redeprog SaaS)
  //   * user.role === 'admin' (rol global Administrador de EducaT)
  //   * user.organizacionId ausente/vacio (no pertenece a ningun tenant)
  // El backend valida lo mismo en RequireSuperAdminRedeprogFilter.
  const isSuperAdminRedeprog = useCallback(
    () => IS_REDEPROG && isAdmin() && !user?.organizacionId,
    [isAdmin, user]
  );

  return (
    <AuthContext.Provider
      value={{
        user, token, loading, isAuthenticated,
        login, logout, refreshSession, refreshProfile,
        hasRole, isAdmin, isInstructor, isStudent,
        hasEcolabRole, isFenagroSuperAdmin,
        isSuperAdminRedeprog,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}

// ═══════════════════════════════════════════════════════════════════════
//  Wrapper — usar en index.js envolviendo <App />
// ═══════════════════════════════════════════════════════════════════════
export function AppProviders({ children }) {
  return <AuthProvider>{children}</AuthProvider>;
}
